import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { withRouter } from 'react-router-dom';
import {
  Grid,
  InputLabel,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  MenuItem,
  ListItemText,
  Checkbox,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import MultiSelect from '../../common/multi-select';
import DaysSelector from '../../common/days-selector';
import RunMap from './run-map';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);

function BaseEditRunSchedule({
  currentRunSchedule,
  handleChange,
  selectedDate,
  setSelectedDate,
  startTime,
  setStartTime,
  handleCountChange,
  sortedRepeatOptions,
  allVehiclesToggleOptions,
  handleIncludeAllVehiclesChange,
  displayStatic,
  showAssignees,
  setShowAssignees,
  assignees,
  assigneeOptions,
  handleWranglerChange,
  zoneOptions,
  handleRouteChange,
  handleZonesChange,
  editing,
  sortedStatusOptions,
  selectedMarket,
  getVehiclesAndLocations,
  tz,
  runScheduleTypeOptions,
  styles,
}) {
  return (
    <Grid style={styles.flexRow}>
      <Grid item container style={styles.content} xs={10}>
        <Grid item xs={12} style={styles.runNameLabel}>
          <InputLabel>Name</InputLabel>
          <TextField
            autoFocus
            fullWidth
            margin="none"
            type="text"
            variant="standard"
            name="name"
            value={currentRunSchedule?.name || ''}
            onChange={handleChange}
          />
        </Grid>
        <Grid
          item
          container
          xs={12}
          style={{ paddingTop: 8, paddingBottom: 8 }}
        >
          <Grid item xs={4} style={{ paddingRight: 2 }}>
            <InputLabel>Scheduled Time</InputLabel>
            <TextField
              fullWidth
              id="date"
              type="date"
              variant="standard"
              name="selectedDate"
              placeholder="mmm-dd-yy"
              format="YYYY-MM-DD"
              value={selectedDate}
              InputLabelProps={{
                shrink: true,
                min: dayjs().format('YYYY-MM-DD'),
              }}
              InputProps={{ disableUnderline: true }}
              onChange={(e) => setSelectedDate(e.target.value)}
              sx={{ border: 0, marginRight: 1, marginTop: 0.25 }}
              disabled={currentRunSchedule?.repeat}
            />
          </Grid>
          <Grid item xs={4} style={{ paddingLeft: 7, paddingRight: 0 }}>
            <Tooltip
              placement="right"
              title={tz}
              sx={{ ...styles.tooltip, height: 1, margin: 0 }}
            >
              <InfoIcon sx={{ paddingLeft: 1, margin: 0 }} color="action" />
            </Tooltip>
            <TextField
              id="time"
              type="time"
              variant="standard"
              name="startTime"
              placeholder="--:-- --"
              format="HH:mm"
              value={startTime}
              InputLabelProps={{
                shrink: true,
                min: dayjs.utc().format('HH:mm'),
              }}
              InputProps={{ disableUnderline: true }}
              onChange={(e) => setStartTime(e.target.value)}
              sx={{ paddingLeft: 2, marginTop: 0 }}
            />
          </Grid>
          <Grid
            item
            xs={4}
            style={{ paddingLeft: 2, paddingRight: 0 }}
          >
            <InputLabel sx={{ paddingLeft: 2 }}>Shift Length</InputLabel>
            <TextField
              margin="dense"
              type="number"
              variant="standard"
              name="duration"
              value={currentRunSchedule?.duration || ''}
              InputProps={{
                inputProps: { step: 0.5, min: 0 },
                disableUnderline: true,
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ '&.MuiInputAdornment-root': { marginLeft: 0, paddingLeft: 0 } }}
                  >
                    hours
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 80,
                marginTop: 0.25,
                marginLeft: 2,
                justifyContent: 'flex-start',
              }}
              onChange={handleCountChange}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <FormControl sx={{ minWidth: '100%', width: '100%' }}>
            <InputLabel style={styles.runTypeLabel}>Repeat</InputLabel>
            <Select
              fullWidth
              value={currentRunSchedule?.repeat}
              label="Repeat"
              name="repeat"
              onChange={(e) => {
                handleChange(e);
                setSelectedDate('');
              }}
            >
              {sortedRepeatOptions.map((option) => (
                <MenuItem
                  key={option.label}
                  value={option.value}
                >
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {currentRunSchedule?.repeat && (
          <Grid item xs={12}>
            <DaysSelector
              value={currentRunSchedule?.days}
              onChange={handleChange}
              name="days"
              classes={styles}
            />
          </Grid>
        )}
        {!displayStatic && (
          <>
            <Grid item xs={12}>
              <FormControl sx={{ minWidth: '100%', width: '100%' }}>
                <InputLabel style={styles.runTypeLabel}>Type</InputLabel>
                <Select
                  fullWidth
                  value={currentRunSchedule?.type || ''}
                  label="Type"
                  name="type"
                  onChange={handleChange}
                  sx={{ minWidth: '100%', width: '100%' }}
                >
                  {runScheduleTypeOptions.map((option) => (
                    <MenuItem
                      key={option.label}
                      value={option.value}
                    >
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={{ pt: '3%', pb: '2%' }}>
                Special events in
              </InputLabel>
              <MultiSelect
                placeholder="Select Locations"
                value={currentRunSchedule?.prioritizedZoneIds || []}
                options={zoneOptions}
                handleChange={handleZonesChange}
              />
            </Grid>
          </>
        )}
        {displayStatic && (
          <Grid item xs={12} style={{ paddingBottom: 12 }}>
            <InputLabel sx={{ pb: '2%' }}>Deployment Route</InputLabel>
            <MultiSelect
              placeholder="Select Route(s)"
              value={currentRunSchedule?.routeIds || []}
              options={zoneOptions}
              handleChange={(value) => handleRouteChange(value)}
            />
          </Grid>
        )}
        {!displayStatic && (
          <Grid item xs={12}>
            <InputLabel sx={{ pt: '3%', pb: '2%' }}>Capacity per van</InputLabel>
            <TextField
              autoFocus
              fullWidth
              margin="none"
              type="number"
              variant="standard"
              name="vanCapacity"
              value={currentRunSchedule?.vanCapacity || ''}
              error={currentRunSchedule?.vanCapacity < 1}
              InputProps={{ inputProps: { min: 1 } }}
              onChange={handleCountChange}
              sx={{ paddingBottom: 2 }}
            />
          </Grid>
        )}
        {!displayStatic && (
          <Grid item xs={12}>
            <FormControl sx={{ paddingBottom: 1 }}>
              <FormLabel>Specify the number of available vans?</FormLabel>
              <RadioGroup
                row
                value={currentRunSchedule?.includeAllVehicles}
                name="includeAllVehicles"
                onChange={handleIncludeAllVehiclesChange}
              >
                {allVehiclesToggleOptions.map((option) => (
                  <FormControlLabel
                    key={option.label}
                    value={option.value}
                    control={<Radio />}
                    label={option.label === 'No' ? (
                      <Typography style={styles.flexRow}>
                        {option.label}
                        <Tooltip
                          placement="right-start"
                          title="Deploy all eligible vehicles at the scheduled start time"
                          sx={styles.tooltip}
                        >
                          <InfoIcon sx={{ paddingLeft: 1 }} color="action" />
                        </Tooltip>
                      </Typography>
                    ) : option.label}
                    sx={{ paddingRight: 5 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
        )}
        {!displayStatic && !currentRunSchedule?.includeAllVehicles && (
          <Grid item xs={12}>
            <InputLabel>Number of Vans</InputLabel>
            <TextField
              autoFocus
              fullWidth
              margin="none"
              type="number"
              variant="standard"
              name="vanCount"
              value={currentRunSchedule?.vanCount || ''}
              error={currentRunSchedule?.vanCount < 1}
              InputProps={{ inputProps: { min: 1 } }}
              onChange={handleCountChange}
            />
          </Grid>
        )}
        {displayStatic && (
          <Grid item xs={12}>
            <InputLabel>Quantity to deploy</InputLabel>
            <TextField
              autoFocus
              fullWidth
              margin="none"
              type="number"
              variant="standard"
              name="deployQuantity"
              value={currentRunSchedule?.deployQuantity || ''}
              error={currentRunSchedule?.deployQuantity < 1}
              InputProps={{ inputProps: { min: 1 } }}
              onChange={handleCountChange}
            />
          </Grid>
        )}
        {displayStatic && (
          <Grid item xs={12}>
            <FormControl sx={{ left: 1, top: 0.5, bottom: 0.5 }}>
              <FormControlLabel
                label="Assign OA in Advance"
                labelPlacement="end"
                control={(
                  <Checkbox
                    disableRipple
                    checked={showAssignees}
                    onChange={() => setShowAssignees(!showAssignees)}
                    color="primary"
                  />
                )}
              />
            </FormControl>
            {showAssignees && (
              <MultiSelect
                placeholder="Select An OA"
                handleChange={(value) => handleWranglerChange(value)}
                value={currentRunSchedule?.wranglerIds || [assignees]}
                options={assigneeOptions}
                style={{ paddingTop: '1rem' }}
              />
            )}
          </Grid>
        )}
        {editing && (
          <Grid item xs={12} sx={{ marginTop: 2 }}>
            <FormControl sx={{ minWidth: '100%', width: '100%' }}>
              <InputLabel style={styles.runTypeLabel}>Status</InputLabel>
              <Select
                fullWidth
                value={currentRunSchedule?.status}
                label="Status"
                name="status"
                onChange={handleChange}
                sx={{ height: 40 }}
              >
                {sortedStatusOptions.map((option) => (
                  <MenuItem
                    key={option.label}
                    value={option.value}
                  >
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>
      {displayStatic && (
        <RunMap
          vehiclesAndLocations={getVehiclesAndLocations(currentRunSchedule)}
          selectedMarket={selectedMarket}
        />
      )}
    </Grid>
  );
}

const EditRunSchedule = withRouter(BaseEditRunSchedule);
export { BaseEditRunSchedule, EditRunSchedule };
